import React, { useContext } from 'react'
import { ChangePresentContext, MedPlanContext } from '../../SingleEvent'
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material'
import PresetsTextField from '../../../../../components/Forms/PresetsTextField'

const martynsLawTierOptions = [
  'Not in scope / not assessed',
  'Standard tier',
  'Enhanced tier / qualifying event',
]

const MartynsLaw = () => {
  const { medPlan, setMedPlan } = useContext(MedPlanContext)
  const { changesPresent, setChangesPresent } = useContext(ChangePresentContext)
  const isInScope =
    medPlan?.martynsLawTier === 'Standard tier' ||
    medPlan?.martynsLawTier === 'Enhanced tier / qualifying event'

  const setField = (key) => (value) => {
    setMedPlan((prev) => ({
      ...prev,
      [key]: value,
    }))
    !changesPresent && setChangesPresent(true)
  }

  return (
    <>
      <Grid item xs={12}>
        <Alert severity="info">
          Martyn&apos;s Law uses standard and enhanced tiers. Qualifying public
          events of 800 or more people are treated as enhanced tier and should
          document public protection procedures and measures.
        </Alert>
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          select
          fullWidth
          label="Martyn's Law Tier"
          value={medPlan?.martynsLawTier ?? ''}
          onChange={(e) => setField('martynsLawTier')(e.target.value)}
          InputLabelProps={{ shrink: true }}
        >
          {martynsLawTierOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {isInScope && (
        <>
          <Grid item xs={12} lg={4}>
        <TextField
          fullWidth
          label="Responsible Person / Organisation"
          value={medPlan?.martynsLawResponsiblePerson ?? ''}
          onChange={(e) =>
            setField('martynsLawResponsiblePerson')(e.target.value)
          }
          InputLabelProps={{ shrink: !!medPlan?.martynsLawResponsiblePerson }}
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          fullWidth
          label="Senior Individual"
          value={medPlan?.martynsLawSeniorIndividual ?? ''}
          onChange={(e) =>
            setField('martynsLawSeniorIndividual')(e.target.value)
          }
          InputLabelProps={{ shrink: !!medPlan?.martynsLawSeniorIndividual }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <PresetsTextField
          fullWidth
          multiline
          minRows={2}
          label="Attendance Assessment Basis"
          value={medPlan?.martynsLawAttendanceBasis}
          suggestionPath="medPlan.martynsLawAttendanceBasis"
          suggestionLabel="Attendance assessments from templates"
          setValue={setField('martynsLawAttendanceBasis')}
          InputLabelProps={{ shrink: !!medPlan?.martynsLawAttendanceBasis }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <PresetsTextField
          fullWidth
          multiline
          minRows={2}
          label="SIA Notification / Evidence"
          value={medPlan?.martynsLawSiaNotification}
          suggestionPath="medPlan.martynsLawSiaNotification"
          suggestionLabel="SIA notification notes from templates"
          setValue={setField('martynsLawSiaNotification')}
          InputLabelProps={{ shrink: !!medPlan?.martynsLawSiaNotification }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <PresetsTextField
          fullWidth
          multiline
          minRows={2}
          label="Evacuation Procedure"
          value={medPlan?.martynsLawEvacuation}
          suggestionPath="medPlan.martynsLawEvacuation"
          suggestionLabel="Evacuation procedures from templates"
          setValue={setField('martynsLawEvacuation')}
          InputLabelProps={{ shrink: !!medPlan?.martynsLawEvacuation }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <PresetsTextField
          fullWidth
          multiline
          minRows={2}
          label="Invacuation / Shelter Procedure"
          value={medPlan?.martynsLawInvacuation}
          suggestionPath="medPlan.martynsLawInvacuation"
          suggestionLabel="Invacuation procedures from templates"
          setValue={setField('martynsLawInvacuation')}
          InputLabelProps={{ shrink: !!medPlan?.martynsLawInvacuation }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <PresetsTextField
          fullWidth
          multiline
          minRows={2}
          label="Lockdown Procedure"
          value={medPlan?.martynsLawLockdown}
          suggestionPath="medPlan.martynsLawLockdown"
          suggestionLabel="Lockdown procedures from templates"
          setValue={setField('martynsLawLockdown')}
          InputLabelProps={{ shrink: !!medPlan?.martynsLawLockdown }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <PresetsTextField
          fullWidth
          multiline
          minRows={2}
          label="Public Alerting & Communication"
          value={medPlan?.martynsLawPublicComms}
          suggestionPath="medPlan.martynsLawPublicComms"
          suggestionLabel="Public communication procedures from templates"
          setValue={setField('martynsLawPublicComms')}
          InputLabelProps={{ shrink: !!medPlan?.martynsLawPublicComms }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <PresetsTextField
          fullWidth
          multiline
          minRows={2}
          label="Protective Security Measures"
          value={medPlan?.martynsLawProtectiveMeasures}
          suggestionPath="medPlan.martynsLawProtectiveMeasures"
          suggestionLabel="Protective measures from templates"
          setValue={setField('martynsLawProtectiveMeasures')}
          InputLabelProps={{ shrink: !!medPlan?.martynsLawProtectiveMeasures }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <PresetsTextField
          fullWidth
          multiline
          minRows={2}
          label="Search, Screening & Hostile Vehicle Mitigation"
          value={medPlan?.martynsLawScreeningHvm}
          suggestionPath="medPlan.martynsLawScreeningHvm"
          suggestionLabel="Screening and HVM notes from templates"
          setValue={setField('martynsLawScreeningHvm')}
          InputLabelProps={{ shrink: !!medPlan?.martynsLawScreeningHvm }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <PresetsTextField
          fullWidth
          multiline
          minRows={2}
          label="Security of Sensitive Information"
          value={medPlan?.martynsLawInformationSecurity}
          suggestionPath="medPlan.martynsLawInformationSecurity"
          suggestionLabel="Information security notes from templates"
          setValue={setField('martynsLawInformationSecurity')}
          InputLabelProps={{
            shrink: !!medPlan?.martynsLawInformationSecurity,
          }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <PresetsTextField
          fullWidth
          multiline
          minRows={2}
          label="Staff Briefing & Training"
          value={medPlan?.martynsLawStaffBriefing}
          suggestionPath="medPlan.martynsLawStaffBriefing"
          suggestionLabel="Staff briefing notes from templates"
          setValue={setField('martynsLawStaffBriefing')}
          InputLabelProps={{ shrink: !!medPlan?.martynsLawStaffBriefing }}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          label="Multi-agency co-ordination agreed with venue, organiser, security and emergency services"
          control={
            <Checkbox
              checked={medPlan?.martynsLawCoordinationConfirmed ?? false}
              onChange={(e) =>
                setField('martynsLawCoordinationConfirmed')(e.target.checked)
              }
              color="success"
            />
          }
        />
      </Grid>
        </>
      )}
    </>
  )
}

export default React.memo(MartynsLaw)
